import React, { useMemo, useState } from 'react';

import { useLazyGetApproveRequestedExercises } from '../../../services/redux/api/exercisesAPI';
import ExerciseList from '../exercise-public-requests/exercise-list/ExerciseList';
import { useExercise } from '../useExercise';
import ExercisesListWithPanel from '../exercises-list-with-panel/ExercisesListWithPanel';
import Exercise from '../exercise/Exercise';
import NewExercise from '../new-exercise/NewExercise';
import type { ExercisePanelMode } from '../exercises-list-with-panel/ExercisesListWithPanel';
import { IExercise } from '../../../types/interfaces/exercise';
import { Box } from '@mui/material';
import AppSelectWrapper from '../../../components/designe/app-select-wrapper/AppSelectWrapper';
import { useTranslation } from 'react-i18next';
import { useStyles } from './ExercisePublicRequests.styles';
import theme from '../../../styles/theme';
import Search from '../../../components/reusable/search/Search';
import { useLazyLoadGeneral } from '../../../hooks/use-lazy-load-general';
import { useDebounce } from '../../../hooks/use-debounce';

const NULL_OWNER_USER_FILTER_ID = '__null-owner__';

export default function ExercisePublicRequests() {
  const { t } = useTranslation();
  const { classes } = useStyles(theme);
  const [filter, setFilter] = useState('');
  const debouncedFilter = useDebounce(filter, 500);
  const { items: exercises, footerRef } = useLazyLoadGeneral<IExercise>(
    useLazyGetApproveRequestedExercises,
    debouncedFilter,
    15,
  );
  const { renderExerciseActionButton } = useExercise();
  const [userFilterId, setUserFilterId] = useState<string>('all');

  const users = useMemo(() => {
    const usersMap = new Map<string, { id: string; name: string }>();
    let hasNullOwner = false;

    exercises?.forEach((exercise) => {
      if (!exercise.owner?.id) {
        hasNullOwner = true;
        return;
      }

      usersMap.set(exercise.owner.id, {
        id: exercise.owner.id,
        name: `${exercise.owner.firstname || ''} ${
          exercise.owner.lastname || ''
        }`.trim(),
      });
    });

    const usersList = [...usersMap.values()].filter((user) => !!user.name);

    if (!hasNullOwner) {
      return usersList;
    }

    return [
      {
        id: NULL_OWNER_USER_FILTER_ID,
        name: t('moderatorDashboard.publicOwnerFilter_public'),
      },
      ...usersList,
    ];
  }, [exercises, t]);

  const filteredExercises = useMemo(
    () =>
      exercises?.filter((exercise) => {
        if (userFilterId === 'all') {
          return true;
        }

        if (userFilterId === NULL_OWNER_USER_FILTER_ID) {
          return !exercise.owner?.id;
        }

        return exercise.owner?.id === userFilterId;
      }) ?? [],
    [exercises, userFilterId],
  );

  const [panelState, setPanelState] = useState<{
    exerciseId: string | null;
    mode: ExercisePanelMode;
  }>({ exerciseId: null, mode: 'view' });

  const panelOpen = panelState.exerciseId !== null;

  function openPanel(exerciseId: string | null, mode: ExercisePanelMode) {
    setPanelState({ exerciseId, mode });
  }

  function closePanel() {
    setPanelState({ exerciseId: null, mode: 'view' });
  }

  function onEditClick(id: string) {
    openPanel(id, 'edit');
  }

  function onExerciseClick(exercise: IExercise) {
    openPanel(exercise.id, 'view');
  }

  const listContent = (
    <Box>
      <Search
        fullWidth
        value={filter}
        onChange={setFilter}
        className={classes.exercisesListSearch}
      />
      <Box className={classes.filtersRow}>
        <AppSelectWrapper
          label={t('common.user')}
          defaultValue={
            [{ id: 'all', name: t('common.all') }, ...users].find(
              (option) => option.id === userFilterId,
            ) || null
          }
          options={[{ id: 'all', name: t('common.all') }, ...users]}
          onChange={(e) => setUserFilterId(e?.target?.value?.id || 'all')}
          disableClear
          disableSorting
        />
      </Box>
      <ExerciseList
        exercises={filteredExercises}
        showSearch={false}
        getActionButtons={(exercise) =>
          renderExerciseActionButton(exercise, onEditClick)
        }
        onExerciseClick={onExerciseClick}
      />
      <Box ref={footerRef} mb={1} />
    </Box>
  );

  const panelContent = panelOpen ? (
    panelState.mode === 'edit' ? (
      <NewExercise exerciseId={panelState.exerciseId} onBack={closePanel} />
    ) : (
      <Exercise
        exerciseId={panelState.exerciseId}
        onBack={closePanel}
        onEditClick={() =>
          panelState.exerciseId && openPanel(panelState.exerciseId, 'edit')
        }
      />
    )
  ) : null;

  return (
    <ExercisesListWithPanel
      listContent={listContent}
      panelContent={panelContent}
      panelOpen={panelOpen}
      onPanelClose={closePanel}
    />
  );
}
